import useAsync, { TUseAsyncCallback } from "../useAsync";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { DependencyList, useRef } from "react";
import { useIsMounted, useSettings, useUpdateEffect } from "..";
import { UseAxiosOptions } from "./useAxios.types";

const DEFAULT_OPTIONS = {
    headers: { "Content-Type": "application/json" },
};

export default function useAxios<TData = any, TError = any>(
    method: "get" | "post" | "put" | "delete",
    url: string,
    params: { [key: string]: any } | (() => { [key: string]: any }) = {},
    dependencies: DependencyList = [],
    { onSuccess, onError, onStatus, ...options }: UseAxiosOptions<TData> = {}
) {
    const isMounted = useIsMounted();
    //const runAgain = useRef<string[]>([]);
    const runAgain = useRef(false);
    const runAgainCallback = useRef<TUseAsyncCallback<any> | undefined>();
    const settings = useSettings();

    const callback = () => {
        let data = params;
        let options: AxiosRequestConfig = {
            ...DEFAULT_OPTIONS,
        };
        if (method === "get") {
            options.params = params;
            data = options;
        }

        const defaultSearchStringsObj = {} as Record<string, any>;

        if(settings.universe){
            defaultSearchStringsObj.universe = settings.universe;
        }

        if(settings.the_dealer?.id){
            defaultSearchStringsObj.the_dealer = settings.the_dealer.id;
        }

        //@ts-ignore
        const defaultQueryStrings = new URLSearchParams(defaultSearchStringsObj).toString();

        const urlWithUniverse = url + (defaultQueryStrings ? "?" + defaultQueryStrings : "");
        return axios[method](urlWithUniverse, data, options);
    };

    const { runWithCallack, status, value, error, ...response } = useAsync<
        AxiosResponse<TData>,
        AxiosError<TError>
    >(
        (status) => {
            if (status === "loading") {
                runAgain.current = true;
                runAgainCallback.current = callback;
                return;
            }

            return callback();
        },
        dependencies,
        {
            ...options,
            finally: () => {
                if (runAgain.current) {
                    runAgain.current = false;
                    runWithCallack(runAgainCallback.current);
                }
            },
        }
    );

    useUpdateEffect(() => {
        if (!isMounted()) {
            return;
        }

        onStatus?.(status, status === "success" ? value : error);

        if (status === "success") {
            if (value.status === 200) {
                onSuccess?.(value.data, value);
            }
        }
        if (status === "error") {
            onError?.(error);
        }
    }, [status]);

    return { runWithCallack, status, value, error, ...response };
}
